import * as React from "react"
import { useQuery, useMutation } from "@apollo/client"
import { ErrorBoundary } from "react-error-boundary"
import { useRecoilState, useRecoilValue } from "recoil"
import { AccountAdmin, CoordinateSystemUnit, PolicyType } from "../../types"
import {
  GET_CONFIG,
  CREATE_PROJECT,
  ELIGIBLE_ACCOUNTS,
  GET_CRSUNIT,
  GET_GLOBAL_SETTINGS,
  HAS_PROJECT_OWNER_RIGHTS,
  GET_ALLOWED_PROJECT_REGIONS,
  GET_REGIONS_FROM_SETTINGS_API,
  GET_ACCOUNT_ADMINS,
  IS_ALLOWED_TO_ENABLE_AND_REMOVE_EXTENSION,
  IS_CUSTOMER_HANDLED_BY_TCPS
} from "../../api/QuadriApi"
import ConnectApi, { ConnectData } from "../../api/ConnectApi"
import {
  addSnackSelector,
  extensionApiState,
  projectIdState,
  projectOriginState,
  projectRegionState,
  quadriProjectIdState,
  signedInUserState
} from "../StateSelectors"
import { PendingSpinner } from "../Pending"
import { OopsFallback } from "../Oops/OopsFallback"
import QuadriProjectSettings from "./QuadriProjectSettings"
import QuadriFooterNewProject from "./QuadriFooterNewProject"
import { useTranslation } from "react-i18next"
import { exceptionFromApolloError } from "../../util/apolloError"
import { SnackProps } from "../Config/SnackBars"
import { track } from "../../util/gainsight"
import Modal from "../Modal"
import GetEnvironment from "../../environments"
import {
  checkIfAllowedToEnableInProjectRegion,
  checkIfAllowedToEnableOrRemoveExtension,
  getDisplayNamesForRegions
} from "../Config/utils"

const BACKUP_RELOAD_WARNING = 10000
const BACKUP_RELOAD_TIMEOUT = 15000

// Used to determine the config page state. Exported so that the progress banner can use it
export type ConfigStatus = "Creating" | "Active" | "Failed" | "Updating" | "Default" | "Deleting"

// The exported wrapper component that handles error boundary and input
// to the loading component
const ConfigMainNewProject = () => {
  const projectId = useRecoilValue(projectIdState)
  const projectOrigin = useRecoilValue(projectOriginState)
  const signedInUser = useRecoilValue(signedInUserState)

  return (
    <ErrorBoundary FallbackComponent={OopsFallback}>
      <ConfigLoad {...{ projectId, projectOrigin, signedInUser }} />
    </ErrorBoundary>
  )
}

interface ConfigLoadProps {
  projectId?: string
  projectOrigin?: string
  accessToken?: string
  signedInUser?: string
}

// TODO: Shorten this somehow
// The responsibility of this is to load everything necessary to render the
// Config page. It must therefore also hold initial project state as well
// as data from Connect
const ConfigLoad = (props: ConfigLoadProps) => {
  const { projectId, projectOrigin, signedInUser } = props
  const { t } = useTranslation(["common", "config"])

  if (!projectId) throw new Error("Cannot load config page: Missing project id")
  if (!projectOrigin) throw new Error("Cannot load config page: Missing project origin")

  // Need to set this if this is the first time project is created
  const [, setQuadriProjectId] = useRecoilState(quadriProjectIdState)
  const extensionApi = useRecoilValue(extensionApiState)
  const [, setProjectRegionState] = useRecoilState(projectRegionState)
  const [projectStatus, setProjectStatus] = React.useState(undefined as ConfigStatus | undefined)

  // Connect Data
  const [connectApi, setConnectApi] = React.useState<ConnectApi | undefined>(undefined)
  const [connectData, setConnectData] = React.useState<ConnectData>({})
  const [fetchedConnect, setFetchedConnect] = React.useState(false)
  const [ownershipAccount, setOwnershipAccount] = React.useState("")
  const [CrsName, SetCrsName] = React.useState<string>()

  const accountsQuery = useQuery(ELIGIBLE_ACCOUNTS)

  const crsUnitQuery = useQuery(GET_CRSUNIT, {
    variables: { crsName: CrsName ?? "" },
    skip: CrsName === undefined
  })

  const projectQuery = useQuery(GET_CONFIG, {
    variables: { id: projectId },
    onCompleted: (data) => {
      const updatedStatus = data?.tryGetConnectQuadriProject?.project?.projectStatus
      if (typeof updatedStatus !== "undefined") {
        setProjectStatus(updatedStatus)
        console.log("Setting project status: ", updatedStatus)
      }

      const quadriProjectId = data?.tryGetConnectQuadriProject?.project?.quadriProjectId
      if (quadriProjectId) {
        setQuadriProjectId(quadriProjectId)
        console.log("Set Quadri Project Id: ", quadriProjectId)
      }
    }
  })

  const getGlobalSettingsQuery = useQuery(GET_GLOBAL_SETTINGS)

  React.useEffect(() => {
    const runEffectAsync = async () => {
      const api = new ConnectApi(projectOrigin)
      setConnectApi(api)
      const connectData = await api.getConnectProjectData(projectId)
      console.log("Connect data: ", connectData)
      console.log("Connect account: ", connectData.connectProject.license.accounts[0].accountId)
      setConnectData(connectData)
      SetCrsName(connectData.crsName)
      setOwnershipAccount(connectData.connectProject?.license?.accounts[0]?.name)
      setProjectRegionState(connectData.region)
      setFetchedConnect(true)
    }
    runEffectAsync()
  }, [projectOrigin, projectId, setProjectRegionState])

  // "Handle" errors (they are handled by parent error boundary)
  if (accountsQuery.error) throw exceptionFromApolloError(accountsQuery.error)
  if (projectQuery.error) throw exceptionFromApolloError(projectQuery.error)
  if (crsUnitQuery.error) throw exceptionFromApolloError(crsUnitQuery.error)
  if (getGlobalSettingsQuery.error) throw exceptionFromApolloError(getGlobalSettingsQuery.error)

  // From here, the queries are either loading, or they have data
  if (
    !connectApi ||
    !fetchedConnect ||
    accountsQuery.loading ||
    projectQuery.loading ||
    crsUnitQuery.loading ||
    getGlobalSettingsQuery.loading
  ) {
    return <PendingSpinner />
  } else {
    let quadriConfigurationUiUrl
    if (getGlobalSettingsQuery.data) {
      quadriConfigurationUiUrl = getGlobalSettingsQuery.data.getGlobalSettings.quadriConfigurationFrontEndUrl
    }

    // But do we have a project???
    const { found, project } = projectQuery.data.tryGetConnectQuadriProject

    //do we have crsUnit?
    let crsUnit
    if (crsUnitQuery.data) {
      crsUnit = crsUnitQuery.data.getCrsUnit.unitName
    }

    const innerProps = {
      connectProjectId: projectId,
      connectData,
      featureCatalogId: project?.featureCatalogId,
      extensionOwnershipId: connectData.connectProject.license.accounts[0].accountId,
      extensionOwnershipName: connectData.connectProject.license.accounts[0].name,
      coordinateSystemUnit: crsUnit || project?.coordinateSystemUnit,
      projectStatus,
      projectStatusMessage: project?.projectStatusMessage,
      createdAt: project?.createdAt,
      updatedAt: project?.updatedAt,
      permissionsUpdatedAt: project?.permissionsUpdatedAt,
      quadriConfigurationUiUrl,
      setProjectStatus,
      setQuadriProjectId,
      crsUnit,
      signedInUser
    }
    return <ConfigMainInner {...innerProps} />
  }
}

interface ConfigMainInnerProps {
  connectProjectId: string
  connectData: ConnectData
  featureCatalogId?: string
  extensionOwnershipId: string
  extensionOwnershipName: string
  coordinateSystemUnit?: CoordinateSystemUnit
  projectStatus?: ConfigStatus
  projectStatusMessage?: string
  createdAt?: string
  updatedAt?: string
  permissionsUpdatedAt?: string
  quadriConfigurationUiUrl: string
  setProjectStatus: (status: ConfigStatus) => void
  setQuadriProjectId: (quadriProjectId: string) => void
  crsUnit: string | undefined
  signedInUser?: string
}

// This is the inner component responsible for page interaction and rendering
// It is assumed that all required data is loaded when this is rendered
const ConfigMainInner = (props: ConfigMainInnerProps) => {
  const { region } = props.connectData
  const { projectStatus, connectProjectId, signedInUser } = props
  const { setProjectStatus, setQuadriProjectId } = props
  const [, addSnack] = useRecoilState(addSnackSelector)
  const { t } = useTranslation("config")

  const isHandledByTcpsQuery = useQuery(IS_CUSTOMER_HANDLED_BY_TCPS, {
    variables: { customerAccountId: props.extensionOwnershipId },
    skip: !props.extensionOwnershipId
  })

  const isHandledByTcps = isHandledByTcpsQuery.error
    ? false
    : isHandledByTcpsQuery.data?.isCustomerHandledByTcps.isHandledByTcps

  const isHandledByTcpsLoading = !props.extensionOwnershipId ? false : isHandledByTcpsQuery.loading

  // Feature catalog state is kept here and not in child QuadriProjectSettings component
  // because it is used in the creation request that is tied to the button.
  const [extensionOwnership, setExtensionOwnership] = React.useState<string | undefined>(props.extensionOwnershipId)
  const [extensionOwnershipName, setExtensionOwnershipName] = React.useState<string | undefined>(
    props.extensionOwnershipName
  )
  const [featureCatalogId, setFeatureCatalogId] = React.useState<string | undefined>(props.featureCatalogId)
  const [featureCatalogStandard, setFeatureCatalogStandard] = React.useState<string | undefined>(undefined)
  const [coordinateSystemUnit, setCoordinateSystemUnit] = React.useState<CoordinateSystemUnit | undefined>(
    props.coordinateSystemUnit
  )
  const [showMessage, setShowMessage] = React.useState({
    show: false,
    for: "",
    accountName: extensionOwnershipName
  })
  const [hasProjectOwnerAdminRights, setHasProjectOwnerAdminRights] = React.useState(false)
  const [hasProjectOwnerClassicAdminRights, setHasProjectOwnerClassicAdminRights] = React.useState(false)
  const [allowedRegions, setAllowedRegions] = React.useState<string>("")
  const projectRegion = useRecoilValue(projectRegionState)
  const [allowedByRegionPolicy, setAllowedByRegionPolicy] = React.useState<boolean>(true)
  const [showAllAdmins, setShowAllAdmins] = React.useState(false)
  const [isAllowedToEnableAndRemoveExtensionbyPolicy, setIsAllowedToEnableAndRemoveExtensionbyPolicy] =
    React.useState<boolean>(true)
  const [accountAdmins, setAccountAdmins] = React.useState<AccountAdmin[]>([])

  // If project is never created, then this is the mutation that gets sent on button click
  const [createProject, createProjectResponse] = useMutation(CREATE_PROJECT, {
    onCompleted: (data: any) => {
      console.log("On create complete, data: ", data)
      const quadriProjectId = data?.createProject?.projectId
      if (typeof quadriProjectId !== "undefined") {
        setQuadriProjectId(quadriProjectId)
      }
      startBackupTimeoutReload(t, addSnack)
    },
    onError: (error) => {
      setProjectStatus("Failed")
      addSnack({
        type: "error",
        message: error.message ?? "Failed to create the project. Please try again.",
        duration: 3000 // dismiss after 3s
      })
    }
  })

  const {
    data: projectOwnerRightsData,
    loading: projectOwnerRightsDataLoading,
    error: accountRightsDataError
  } = useQuery(HAS_PROJECT_OWNER_RIGHTS, {
    variables: { connectProjectId: connectProjectId, usecases: ["Admin", "ClassicAdmin"], region: region }
  })

  React.useEffect(() => {
    if (!projectOwnerRightsDataLoading && !accountRightsDataError && projectOwnerRightsData) {
      const rights = projectOwnerRightsData?.hasProjectOwnerRights?.rights || []
      const adminRight = rights.find((r: any) => r.usecase === "Admin").right
      const classicAdminRight = rights.find((r: any) => r.usecase === "ClassicAdmin").right
      setHasProjectOwnerAdminRights(adminRight)
      setHasProjectOwnerClassicAdminRights(classicAdminRight)
    }
    if (accountRightsDataError) {
      throw exceptionFromApolloError(accountRightsDataError)
    }
  }, [projectOwnerRightsData, accountRightsDataError, projectOwnerRightsDataLoading])

  const closeMessageModal = () => {
    setShowMessage({ show: false, for: "", accountName: "" })
  }

  const skipRegionPolicy =
    !GetEnvironment().enforceRegionPolicy ||
    !GetEnvironment().enableNewCommonUi ||
    extensionOwnership === undefined ||
    hasProjectOwnerAdminRights

  const skipExtensionActivationPolicy =
    !GetEnvironment().enforceExtensionActivationAndRemovalPolicy ||
    !GetEnvironment().enableNewCommonUi ||
    extensionOwnership === undefined ||
    hasProjectOwnerAdminRights

  //Add query to get region policy
  const regionPolicyQuery = useQuery(GET_ALLOWED_PROJECT_REGIONS, {
    variables: { customerAccountId: extensionOwnership ?? "" },
    skip: skipRegionPolicy || isHandledByTcpsQuery.loading || isHandledByTcps === false
  })

  //Add query to get region from settings api
  const getRegionsFromSettingsApiQuery = useQuery(GET_REGIONS_FROM_SETTINGS_API, {
    skip: skipRegionPolicy
  })

  //Add query to get extension activation and removal policy
  const extensionActivationPolicyQuery = useQuery(IS_ALLOWED_TO_ENABLE_AND_REMOVE_EXTENSION, {
    variables: { customerAccountId: extensionOwnership ?? "" },
    skip: skipExtensionActivationPolicy || isHandledByTcpsQuery.loading || isHandledByTcps === false
  })

  //Add query to get account admins
  const accountAdminsQuery = useQuery(GET_ACCOUNT_ADMINS, {
    variables: { customerAccountId: extensionOwnership ?? "" },
    skip: skipExtensionActivationPolicy || isHandledByTcpsQuery.loading || isHandledByTcps === false
  })

  React.useEffect(() => {
    if (!regionPolicyQuery.loading && !regionPolicyQuery.error && regionPolicyQuery.data) {
      const result = regionPolicyQuery.data?.getAllowedProjectRegions
      if (getRegionsFromSettingsApiQuery.data) {
        const regionsDisplayNames = getRegionsFromSettingsApiQuery.data.getRegions.regions.map((region: any) => {
          return { name: region.name, displayName: region.displayName }
        })
        setAllowedRegions(getDisplayNamesForRegions(result.regions, regionsDisplayNames))
      } else {
        setAllowedRegions(result.regions ? result.regions : "")
      }
      if (projectRegion) {
        setAllowedByRegionPolicy(checkIfAllowedToEnableInProjectRegion(result, projectRegion))
      }
    }
    if (regionPolicyQuery.error) {
      throw exceptionFromApolloError(regionPolicyQuery.error)
    }
  }, [
    regionPolicyQuery.loading,
    regionPolicyQuery.error,
    regionPolicyQuery.data,
    getRegionsFromSettingsApiQuery.data,
    projectRegion
  ])

  React.useEffect(() => {
    if (
      !extensionActivationPolicyQuery.loading &&
      !extensionActivationPolicyQuery.error &&
      extensionActivationPolicyQuery.data &&
      accountAdminsQuery.data
    ) {
      setIsAllowedToEnableAndRemoveExtensionbyPolicy(
        checkIfAllowedToEnableOrRemoveExtension(extensionActivationPolicyQuery.data)
      )
      setAccountAdmins(accountAdminsQuery.data.getAccountAdmins)
    }
    if (extensionActivationPolicyQuery.error) {
      throw exceptionFromApolloError(extensionActivationPolicyQuery.error)
    }

    if (accountAdminsQuery.error) {
      throw exceptionFromApolloError(accountAdminsQuery.error)
    }
  }, [
    accountAdminsQuery.data,
    extensionActivationPolicyQuery.data,
    extensionActivationPolicyQuery.error,
    accountAdminsQuery.error,
    extensionActivationPolicyQuery.loading
  ])

  // Callback function to Create/Sync button
  const submitQuadriProject = (e?: any) => {
    e?.preventDefault()

    if (GetEnvironment().enableNewCommonUi) {
      //On Submit first check here if project is to be activated and if allowed by policy? if yes execute the activation else show message
      if (!isAllowedToEnableAndRemoveExtensionbyPolicy) {
        setShowMessage({
          show: true,
          for: PolicyType.extensionActivationAndRemovalPolicy,
          accountName: extensionOwnershipName
        })
        return
      } else if (!allowedByRegionPolicy) {
        setShowMessage({ show: true, for: PolicyType.regionPolicy, accountName: extensionOwnershipName })
        return
      }
    }

    if (
      connectProjectId !== undefined &&
      featureCatalogId !== undefined &&
      coordinateSystemUnit !== undefined &&
      region !== undefined &&
      extensionOwnership !== undefined
    ) {
      const project = {
        connectProjectId: connectProjectId,
        featureCatalogId,
        coordinateSystemUnit,
        connectProjectRegion: region,
        customerAccountId: extensionOwnership,
        isTcpsLicensed: hasProjectOwnerClassicAdminRights
      }

      track("project_create", { connectProjectId: connectProjectId })
      setProjectStatus("Creating")
      createProject({ variables: { project: project } })
      console.log("Creating")
    } else {
      console.error("Missing create parameters")
    }
  }

  // Disable creation
  const isEnableButtonDisabled = projectStatus === "Creating" || !featureCatalogId || !coordinateSystemUnit
  const disableEditCoordinateSystemUnit = projectStatus === "Creating" || !!props.crsUnit
  const displayedAdmins = showAllAdmins ? accountAdmins : accountAdmins.slice(0, 10)

  return (
    <>
      <div>
        <QuadriProjectSettings
          existingFcId={null}
          fcStandard={featureCatalogStandard}
          setFcStandard={setFeatureCatalogStandard}
          featureCatalogId={featureCatalogId}
          setFeatureCatalogId={setFeatureCatalogId}
          coordinateSystemUnits={coordinateSystemUnit}
          setCoordinateSystemUnits={setCoordinateSystemUnit}
          disableEdit={false}
          disableEditCoordinateSystemUnit={disableEditCoordinateSystemUnit}
          configStatus={"Default"}
          projectStatus={undefined}
          region={region}
          quadriConfigurationUiUrl={props.quadriConfigurationUiUrl}
          onChange={() => {}}
          isHandledByTcps={isHandledByTcps}
          isHandledByTcpsLoading={isHandledByTcpsLoading}
        />
        <QuadriFooterNewProject
          configStatus={projectStatus ?? "Default"}
          isEnableButtonDisabled={isEnableButtonDisabled}
          submitQuadriProject={submitQuadriProject}
        />
        {GetEnvironment().enableNewCommonUi && signedInUser && (
          <>
            {showMessage.show && showMessage.for === PolicyType.extensionActivationAndRemovalPolicy && (
              <Modal heading={t("extensionPolicy.heading")} closeModal={closeMessageModal} large={true}>
                <div>{t("extensionPolicy.enable") + showMessage.accountName + t("extensionPolicy.commonText")}</div>
                <br />
                <ul>
                  {displayedAdmins.map((admin: AccountAdmin, index: number) => (
                    <li key={index}>
                      <strong>{admin.fullName}</strong> ({admin.email})
                    </li>
                  ))}
                </ul>
                {/* Show "Show More" or "Show Less" link based on admin count */}
                {accountAdmins.length > 10 && !showAllAdmins && (
                  <button
                    className="m-0 p-0"
                    onClick={() => setShowAllAdmins(true)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#005F9E" }}
                  >
                    {t("extensionPolicy.showMore")}
                  </button>
                )}
                {showAllAdmins && (
                  <button
                    onClick={() => setShowAllAdmins(false)}
                    className="m-0 p-0"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#005F9E" }}
                  >
                    {t("extensionPolicy.showLess")}
                  </button>
                )}
              </Modal>
            )}
            {showMessage.show && showMessage.for === PolicyType.regionPolicy && (
              <Modal heading={t("regionPolicy.heading")} closeModal={closeMessageModal} large={true}>
                <div>{t("regionPolicy.enable") + " " + allowedRegions}</div>
              </Modal>
            )}
          </>
        )}
      </div>
    </>
  )
}

export const startBackupTimeoutReload = (t: any, addSnack: (props: SnackProps) => void) => {
  const snackTimer = setTimeout(() => {
    console.log(`Warning snackbar since no kafka-notification was received after ${BACKUP_RELOAD_WARNING} ms`)
    addSnack(getBackupReloadWarnSnackProps(t, BACKUP_RELOAD_WARNING))
  }, BACKUP_RELOAD_WARNING)
  clearTimeout(snackTimer)
  const reloadtimer = setTimeout(() => {
    console.log(`Reloading page since no kafka-notification was received after ${BACKUP_RELOAD_TIMEOUT} ms`)
    window.location.reload()
  }, BACKUP_RELOAD_TIMEOUT)
  clearTimeout(reloadtimer)
}

const getBackupReloadWarnSnackProps = (t: any, timeMs: number): SnackProps => ({
  type: "warning",
  message: t("backupReloadWarning.warning")
})

export default ConfigMainNewProject
